import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal, { MaskedLine } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import Book3D from "@/components/Book3D";
import QuoteCard from "@/components/QuoteCard";
import { SignupForm } from "@/components/Forms";
import { DailyFeature } from "@/pages/Daily";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useSite } from "@/lib/SiteContext";
import { api, assetUrl, track } from "@/lib/api";
import { kindleLink } from "@/components/Navbar";

const CATHEDRAL_LIGHT =
  "https://images.unsplash.com/photo-1715534231620-670c16afc3a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxkYXJrJTIwbW9vZHklMjBjYXRoZWRyYWwlMjBsaWdodCUyMGFyY2hpdGVjdHVyZSUyMHN0b25lJTIwd2luZG93JTIwc2hhZG93fGVufDB8fHx8MTc5MTM5OTQxOHww&ixlib=rb-4.1.0&q=85";
const CATHEDRAL_CORRIDOR =
  "https://images.pexels.com/photos/14393789/pexels-photo-14393789.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function headlineLines(text) {
  const parts = (text || "").split(/(?<=\.)\s+/).filter(Boolean);
  const lines = [];
  for (const p of parts) {
    const words = p.split(" ");
    if (p.length > 18) {
      const mid = Math.ceil(words.length / 2);
      lines.push(words.slice(0, mid).join(" "), words.slice(mid).join(" "));
    } else {
      lines.push(p);
    }
  }
  return lines;
}

function Hero({ settings, volume, hasTrailer }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bookY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const buy = kindleLink(volume);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden bg-ink pt-28 pb-20" data-testid="hero-section">
      <div className="absolute inset-0" aria-hidden="true">
        <img src={CATHEDRAL_LIGHT} alt="" className="w-full h-full object-cover opacity-25" loading="eager" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 68% 42%, rgba(183,31,50,0.16) 0%, rgba(8,8,8,0.82) 58%, #080808 100%)" }} />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center w-full">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <Reveal y={16}>
            <p className="eyebrow mb-6" data-testid="hero-kicker">
              {settings.hero_kicker || "A Christian Nonfiction Series"}
            </p>
          </Reveal>
          <h1 className="font-display font-black uppercase text-paper leading-[1.02] tracking-tight text-[clamp(1.75rem,4.6vw,3.9rem)]" data-testid="hero-headline">
            {headlineLines(settings.hero_headline).map((line, i) => (
              <MaskedLine key={i} delay={0.12 + i * 0.14}>
                <span className="whitespace-nowrap">{line}</span>
              </MaskedLine>
            ))}
          </h1>
          <Reveal delay={0.55}>
            <p className="font-reading text-paper text-lg sm:text-xl leading-relaxed mt-8 max-w-xl" style={{ textShadow: "0 1px 12px rgba(0,0,0,0.9)" }} data-testid="hero-sub">
              {settings.hero_sub}
            </p>
          </Reveal>
          <Reveal delay={0.7} className="flex flex-wrap items-center gap-4 mt-10">
            {buy && (
              <a
                href={buy}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="amazon-purchase-btn"
                onClick={() => track("purchase_click", "hero")}
                className="inline-flex items-center gap-3 font-ui text-xs uppercase tracking-[0.25em] font-semibold bg-crimson text-paper px-8 py-4 rounded-full transition-[background-color,transform] duration-300 hover:bg-crimsonhi hover:scale-[1.03] active:scale-[0.98]"
              >
                Get the Book on Amazon <ArrowRight size={15} />
              </a>
            )}
            {hasTrailer ? (
              <a
                href="#trailer"
                data-testid="hero-explore-btn"
                className="inline-flex items-center gap-3 font-ui text-xs uppercase tracking-[0.25em] text-paper/80 border border-paper/25 px-8 py-4 rounded-full transition-[border-color,color] duration-300 hover:border-paper/70 hover:text-paper"
              >
                Watch the Trailer
              </a>
            ) : (
              <Link
                to="/book"
                data-testid="hero-explore-btn"
                className="inline-flex items-center gap-3 font-ui text-xs uppercase tracking-[0.25em] text-paper/80 border border-paper/25 px-8 py-4 rounded-full transition-[border-color,color] duration-300 hover:border-paper/70 hover:text-paper"
              >
                Explore the Book
              </Link>
            )}
          </Reveal>
        </motion.div>
        <motion.div style={{ y: bookY }} className="lg:col-span-5 flex justify-center lg:justify-end">
          <Reveal delay={0.35} y={50}>
            <Book3D cover={settings.cover_url} spine={settings.spine_url} back={settings.back_url} title={volume.title || "Dirty Church Chronicles"} author={settings.author_name || "Jermaine Miller"} />
          </Reveal>
        </motion.div>
      </div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2" aria-hidden="true">
        <span className="font-ui text-[9px] tracking-[0.4em] text-paper/35 uppercase">Scroll</span>
        <span className="w-px h-10 bg-gradient-to-b from-crimson to-transparent" />
      </div>
    </section>
  );
}

function TrailerSection() {
  const [items, setItems] = useState([]);
  const [playing, setPlaying] = useState(null);
  useEffect(() => {
    api.get("/public/cinema").then((r) => setItems(r.data.homepage || [])).catch(() => {});
  }, []);
  if (!items.length) return null;
  const feature = items[0];
  return (
    <section id="trailer" className="relative bg-coal py-24 sm:py-32 overflow-hidden" data-testid="home-trailer-section">
      <div className="absolute inset-0 crimson-floor" aria-hidden="true" />
      <div className="relative max-w-6xl mx-auto px-6 sm:px-10">
        <Reveal>
          <p className="eyebrow mb-4">Cinema</p>
          <h2 className="font-display font-bold text-3xl sm:text-5xl text-paper mb-10">Watch the Trailer</h2>
        </Reveal>
        <Reveal delay={0.15}>
          <button
            onClick={() => { setPlaying(feature); track("trailer_play", feature.title); }}
            data-testid="home-trailer-play-btn"
            className="group relative block w-full text-left border border-paper/10 overflow-hidden"
            aria-label={`Play trailer: ${feature.title}`}
          >
            <div className="aspect-video bg-ink relative">
              {feature.poster_url && (
                <img src={assetUrl(feature.poster_url)} alt={`Poster frame for ${feature.title}`} className="w-full h-full object-cover opacity-80 transition-opacity duration-500 group-hover:opacity-100" loading="lazy" />
              )}
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="w-20 h-20 rounded-full bg-crimson/90 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                  <Play size={28} className="text-paper ml-1" />
                </span>
              </span>
            </div>
            <div className="p-6 border-t border-paper/10">
              <p className="font-display text-xl text-paper">{feature.title}</p>
              {feature.caption && <p className="font-ui text-sm text-paper/50 mt-2">{feature.caption}</p>}
            </div>
          </button>
        </Reveal>
        {items.length > 1 && (
          <Reveal delay={0.25}>
            <Link to="/cinema" data-testid="home-cinema-link" className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-[0.25em] text-crimsonhi mt-8 hover:text-paper transition-colors duration-300">
              All films <ArrowRight size={14} />
            </Link>
          </Reveal>
        )}
      </div>
      <Dialog open={!!playing} onOpenChange={() => setPlaying(null)}>
        <DialogContent className="bg-ink border-paper/15 max-w-4xl p-2" data-testid="home-trailer-modal">
          <DialogTitle className="sr-only">{playing?.title}</DialogTitle>
          {playing && (
            playing.kind === "embed" ? (
              <iframe src={playing.url} title={playing.title} className="w-full aspect-video" allow="accelerometer; autoplay; encrypted-media; picture-in-picture" allowFullScreen />
            ) : (
              <video poster={assetUrl(playing.poster_url)} controls preload="none" className="w-full aspect-video bg-black" data-testid="home-trailer-video">
                {playing.webm_url && <source src={assetUrl(playing.webm_url)} type="video/webm" />}
                <source src={assetUrl(playing.url)} type="video/mp4" />
              </video>
            )
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function ExcerptHighlights() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/public/excerpts").then((r) => setItems(r.data.slice(0, 3))).catch(() => {});
  }, []);
  if (!items.length) return null;
  return (
    <section className="bg-ink py-24 sm:py-32 border-t border-paper/10" data-testid="home-excerpts-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <Reveal>
          <p className="eyebrow mb-4">Inside the Chronicles</p>
          <h2 className="font-display font-bold text-3xl sm:text-5xl text-paper mb-14">Approved Readings</h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((ex, i) => (
            <Reveal key={ex.excerpt_id} delay={i * 0.12}>
              <Link
                to={`/chronicles/${ex.excerpt_id}`}
                data-testid={`home-excerpt-${ex.excerpt_id}`}
                className="block p-10 h-full border border-paper/10 group transition-colors duration-500 hover:bg-coal hover:border-paper/25"
              >
                {ex.roman && <span className="font-display text-crimson text-lg tracking-[0.2em]">{ex.roman}</span>}
                <h3 className="font-display text-2xl text-paper mt-3 group-hover:text-crimsonhi transition-colors duration-300">{ex.title}</h3>
                <p className="font-ui text-xs text-paper/40 uppercase tracking-[0.2em] mt-4">{ex.reading_time} min read</p>
              </Link>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <Link to="/chronicles" data-testid="home-all-excerpts-link" className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-[0.25em] text-crimsonhi mt-10 hover:text-paper transition-colors duration-300">
            Enter the reading room <ArrowRight size={14} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function QuoteSection() {
  const [quotes, setQuotes] = useState([]);
  useEffect(() => {
    api.get("/public/quotes").then((r) => setQuotes(r.data)).catch(() => {});
  }, []);
  if (!quotes.length) return null;
  return (
    <section className="bg-ink py-24 sm:py-32 border-t border-paper/10" data-testid="home-quotes-section">
      <div className="max-w-6xl mx-auto px-6 sm:px-10">
        <Reveal>
          <p className="eyebrow mb-4">Share the Word</p>
          <h2 className="font-display font-bold text-3xl sm:text-5xl text-paper mb-14">Quote Cards</h2>
        </Reveal>
        <Reveal delay={0.15}>
          <QuoteCard quotes={quotes} />
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  const { settings, volume, flags } = useSite();
  const buy = kindleLink(volume);
  const coming = volume?.coming || [];

  return (
    <div data-testid="home-page">
      <Seo title={settings.seo_title} description={settings.seo_description} path="/"
        jsonLd={{ "@context": "https://schema.org", "@type": "WebSite", name: "Dirty Church Chronicles", url: "https://dirtychurchchronicles.com" }} />
      <Hero settings={settings} volume={volume} hasTrailer={flags.media > 0} />
      <Marquee items={settings.marquee_items || []} />

      {/* Volume introduction — paper */}
      <section className="paper-section py-24 sm:py-32" data-testid="home-volume-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow eyebrow-light mb-6">Volume One · Christian Nonfiction</p>
            <p className="font-display font-black leading-[0.85] text-ink" style={{ fontSize: "clamp(6rem, 18vw, 15rem)" }} data-testid="home-chronicle-count">
              {volume.chronicle_count || 111}
            </p>
            <p className="font-display text-2xl sm:text-3xl tracking-[0.25em] text-crimson mt-2 uppercase">Chronicles</p>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7">
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-ink mb-6">{volume.title || "Dirty Church Chronicles: Volume I"}</h2>
            <p className="font-reading text-xl sm:text-2xl leading-relaxed text-ink/80" data-testid="home-synopsis">{volume.synopsis}</p>
            <div className="flex flex-wrap gap-4 mt-10">
              {buy && (
                <a href={buy} target="_blank" rel="noopener noreferrer" data-testid="volume-amazon-btn"
                  onClick={() => track("purchase_click", "volume-section")}
                  className="inline-flex items-center gap-3 font-ui text-xs uppercase tracking-[0.25em] font-semibold bg-ink text-paper px-8 py-4 rounded-full transition-[background-color,transform] duration-300 hover:bg-crimson hover:scale-[1.02] active:scale-[0.98]">
                  Get the Book on Amazon <ArrowRight size={15} />
                </a>
              )}
              <Link to="/book" data-testid="volume-details-btn"
                className="inline-flex items-center gap-3 font-ui text-xs uppercase tracking-[0.25em] text-ink border border-ink/30 px-8 py-4 rounded-full transition-colors duration-300 hover:border-ink">
                Book Details
              </Link>
            </div>
            {coming.length > 0 && (
              <p className="font-ui text-xs text-ink/50 mt-6" data-testid="volume-coming-note">
                {coming.join(" and ")} {coming.length > 1 ? "editions are" : "edition is"} in preparation.
              </p>
            )}
          </Reveal>
        </div>
      </section>

      {/* Editorial statement — dark with corridor image */}
      <section className="relative bg-ink py-28 sm:py-40 overflow-hidden" data-testid="home-editorial-section">
        <div className="absolute inset-0" aria-hidden="true">
          <img src={CATHEDRAL_CORRIDOR} alt="" className="w-full h-full object-cover opacity-15" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink via-transparent to-ink" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 sm:px-10">
          <Reveal>
            <p className="eyebrow mb-8">{settings.editorial_title || "The Prophetic Call"}</p>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="font-reading text-2xl sm:text-3xl lg:text-4xl leading-relaxed text-paper/90" data-testid="home-editorial-body">
              {settings.editorial_body}
            </p>
          </Reveal>
        </div>
      </section>

      {flags.media > 0 && <TrailerSection />}

      <DailyFeature />

      {/* Author introduction — paper */}
      <section className="paper-section py-24 sm:py-32" data-testid="home-author-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-4">
            {settings.author_portrait_url ? (
              <img src={assetUrl(settings.author_portrait_url)} alt={`Portrait of ${settings.author_name}`}
                className="w-full aspect-[3/4] object-cover shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)]" data-testid="home-author-portrait" loading="lazy" />
            ) : (
              <div className="w-full aspect-[3/4] bg-ink flex items-center justify-center" data-testid="home-author-portrait">
                <span className="font-display font-black text-6xl text-paper/20 tracking-tight">JM</span>
              </div>
            )}
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-8">
            <p className="eyebrow eyebrow-light mb-4">The Author</p>
            <h2 className="font-display font-bold text-3xl sm:text-5xl text-ink mb-2">{settings.author_name || "Jermaine Miller"}</h2>
            <p className="font-ui text-xs uppercase tracking-[0.25em] text-crimson mb-8">{settings.author_role}</p>
            <p className="font-reading text-xl sm:text-2xl leading-relaxed text-ink/80 max-w-2xl" data-testid="home-author-bio">{settings.author_bio}</p>
            <Link to="/author" data-testid="home-author-link"
              className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-[0.25em] text-crimson mt-8 hover:text-ink transition-colors duration-300">
              Meet the author <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      {flags.excerpts > 0 && <ExcerptHighlights />}
      {flags.quotes > 0 && <QuoteSection />}

      {/* Signup — dark with crimson floor */}
      <section className="relative bg-ink py-24 sm:py-32 overflow-hidden" data-testid="home-signup-section">
        <div className="absolute inset-0 crimson-floor" aria-hidden="true" />
        <div className="relative max-w-2xl mx-auto px-6 sm:px-10">
          <Reveal>
            <SignupForm heading={settings.signup_heading} body={settings.signup_body} />
          </Reveal>
        </div>
      </section>

      {/* Final invitation — paper */}
      <section className="paper-section py-28 sm:py-40 text-left" data-testid="home-final-cta">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <Reveal>
            <p className="eyebrow eyebrow-light mb-6">Volume I — Available Now on Kindle</p>
            <h2 className="font-display font-black uppercase text-ink text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight max-w-4xl" data-testid="home-final-headline">
              Begin the Return.
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="mt-12 flex flex-wrap items-center gap-4">
            {buy && (
              <a href={buy} target="_blank" rel="noopener noreferrer" data-testid="final-amazon-btn"
                onClick={() => track("purchase_click", "final-cta")}
                className="inline-flex items-center gap-3 font-ui text-xs uppercase tracking-[0.25em] font-semibold bg-crimson text-paper px-10 py-5 rounded-full transition-[background-color,transform] duration-300 hover:bg-crimsonhi hover:scale-[1.03] active:scale-[0.98]">
                Get the Book on Amazon <ArrowRight size={15} />
              </a>
            )}
            <Link to="/bookstore" data-testid="final-bookstore-btn"
              className="inline-flex items-center gap-3 font-ui text-xs uppercase tracking-[0.25em] text-ink border border-ink/30 px-10 py-5 rounded-full transition-colors duration-300 hover:border-ink">
              Official Bookstore
            </Link>
            <Link to="/merch" data-testid="final-merch-btn"
              className="inline-flex items-center gap-3 font-ui text-xs uppercase tracking-[0.25em] text-crimson hover:text-ink transition-colors duration-300 px-2 py-5">
              Shop the Official Collection
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
